import { useMemo, useCallback, useState, useRef, useEffect } from "react"
import { FilterIcon } from "shared/icons"
import { Avatar, IconButton, Paper, Slide, Stack, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material"
import {
	DataGrid, DataGridProps,
	GridRowId, GridColDef, GridColumnHeaderParams, 
	gridClasses, useGridApiRef, GridEventListener
} from "@mui/x-data-grid"
import { Loading } from "components/controls/loading"

import { DataRowModel, GridColumnDefMap, GridColumnState, GridFilter, GridOptions } from "./types"
import { isFilterd } from "./util"
import { renderer } from "./renderer"

import { GridColumnManageInspector } from "./components/columnManage"
import { GridFilterInspector } from "./components/filter"

import { GridDownloadDialog, GridDownloadParams } from "./dialog/download"

import { clipCsvData, downloadData } from "components/download/csv"
import { makeDownloadData } from "./download"
import { Theme } from "@emotion/react"
import { useSearch } from "features/search/context"

const stickyColumnStyle = (field: string, left: number) => ({
	[`& .${gridClasses.columnHeader}[data-field="${field}"]`]: {
		position: 'sticky',
		background: 'var(--DataGrid-t-cell-background-pinned)',
		zIndex: 31,
		left
	},
	[`& .${gridClasses.row} > .${gridClasses.cell}[data-field="${field}"]`]: {
		position: 'sticky',
		background: 'var(--DataGrid-t-cell-background-pinned)',
		zIndex: 31,
		left
	},
	[`& .${gridClasses.row}:hover > .${gridClasses.cell}[data-field="${field}"]`]: {
		background: [
			'color-mix(',
				'in srgb,',
				'var(--DataGrid-t-cell-background-pinned) ,',
				'rgba(from var(--DataGrid-t-color-interactive-hover) r g b / 1) ',
				'calc(var(--DataGrid-t-color-interactive-hover-opacity) * 100%)',
			')'
		].join('')
	}
})

type GridFunctions = {
	dataLength: number
	download: () => void
}
export const useGrid = () => {
	const apiRef = useGridApiRef()
	const functionsRef = useRef<GridFunctions | null>(null)
	const search = useSearch()

	const [filters, setFilters] = useState<GridFilter[]>([])
	const [focusedItem, setFocusedItem] = useState<{
		field: string
		rowId: GridRowId
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		value: any
	} | null>(null)
	const [showColumnManage, setShowColumnManage] = useState<boolean>(false)
	const [showFilterManage, setShowFilterManage] = useState<boolean>(false)
	const [options, setOptions] = useState<GridOptions>({
		sisetuLimit: search?.option?.gridSisetuCount ?? 1000,
		kojinLimit: search?.option?.gridKojinCount ?? 1000,
		showKana: false
	})
	return {
		apiRef,
		functionsRef,
		filters, setFilters,
		focusedItem, setFocusedItem,
		showColumnManage, setShowColumnManage,
		showFilterManage, setShowFilterManage,
		options, setOptions
	}
}

type GridProps<T> = {
	saveKey: string
	columnsDef: GridColumnDefMap<T>
	dataList: T[]
	progress: number | undefined
	onActiveColumn?: (field: string) => void
	onSelectedRow?: (data: T | null) => void
	onDblClick?: (data: T) => void
	baseFileName: string
	sheetName: string | undefined
} & Omit<DataGridProps, "columns" | "rows" | "apiRef"> & ReturnType<typeof useGrid>
export const Grid = <T,>({
	saveKey,
	apiRef,
	functionsRef,
	columnsDef,
	dataList,
	progress,
	focusedItem, setFocusedItem,
	onActiveColumn,
	onSelectedRow,
	onDblClick,
	filters, setFilters,
	showColumnManage, setShowColumnManage,
	showFilterManage, setShowFilterManage,
	options,
	baseFileName,
	sheetName,
	...others
}: GridProps<T>) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))

	const [modal, setModal] = useState<'download' | null>(null)
	const [downloading, setDownloading] = useState<boolean>(false)
	const [keepFilterColumns, setKeepFilterColumns] = useState<string[]>([])
	const [columnStyles, setColumnStyles] = useState<object | null>(null)
  const filterSaveTimer = useRef<NodeJS.Timeout | null>(null)

	const [columnsState, setColumnsState] = useState<GridColumnState[]>(
		() => {
			const saved = localStorage.getItem(saveKey)
			if (saved) {
				try {
					const parsed = JSON.parse(saved) as GridColumnState[]
					return parsed.flatMap(p => {
						const column = columnsDef[p.field]
						if (!column || column.hidden === true) return []
						return {
							field: p.field,
							show: p.show ?? true,
							hideable: p.hideable ?? false,
							width: p.width,
							sticky: p.sticky
						}
					})
				} catch(e) {
					console.error(e)
				}
			}
			return Object.entries(columnsDef).flatMap(
				([field, column]) => {
					if (column.hidden === true) return []
					return {
						field,
						show: column.defaultShow ?? true,
						hideable: column.hideable === false ? false : true,
						sticky: column.sticky,
						width: column.width
					}
				}
			)
		}
	)
	useEffect(
		() => {
      if (filterSaveTimer.current) clearTimeout(filterSaveTimer.current)
			filterSaveTimer.current = setTimeout(() => {
				localStorage.setItem(saveKey, JSON.stringify(columnsState))
				//console.log('saved columns state', columnsState)
				filterSaveTimer.current = null
			}, 500)

			const stickyColumns = columnsState.filter(state => state.sticky === true)

			let columnStyles: SxProps<Theme> | null = null
			if (stickyColumns.length > 0) {
				let left = 0
				stickyColumns.forEach(({ field, width }) => {
					columnStyles = {
						...columnStyles,
						...stickyColumnStyle(field, left)
					}
					left += width ?? 0
				})
			}
			setColumnStyles(columnStyles)
		},
		[saveKey, columnsState, setColumnStyles]
	)

	const renderHeader = useCallback(
		(params: GridColumnHeaderParams) => {
			const header: {
				projectNo?: string
				title?: string
				toolTip?: string
			} = {
				title: params.colDef.headerName
			}
			if (params.colDef.headerName && params.field.startsWith('project-')) {
				const [projectNo, title] = params.colDef.headerName.split(':')
				header.projectNo = projectNo
				header.title = title
			}
			return (
				<>
					{header.projectNo && (
						<Avatar sx={{
							width: "1rem", height: "1rem", fontSize: "0.75rem", marginRight: 0.5,
							color: "black", bgcolor: "#f5f5f5", border: '1px solid #dadde9',
							borderRadius: 1
						}}>
							{header.projectNo}
						</Avatar>
					)}
					<Typography component="div" variant="body2" overflow="hidden" textOverflow="ellipsis">{header.title}</Typography>
					{(filters.find(filter => filter.field === params.colDef.field)) ? (
						<IconButton size="small" onClick={(event) => {
							event.stopPropagation()
							setFilters(filters.filter(f => f.field !== params.colDef.field).slice())
						}}>
							<FilterIcon fontSize="small" color="action"/>
						</IconButton>
					) : <></>}
				</>
			)
		},
		[filters, setFilters]
	)

	const columns = useMemo<GridColDef[]>(
		() => {
			const removed: string[] = []
			const columns: GridColDef[] = columnsState.flatMap(
				state => {
					if (!state.show) return []
					const column = columnsDef[state.field]
					if (!column || column.hidden === true) {
						removed.push(state.field)
						return []
					}
					return {
						field: state.field,
						headerAlign: "center",
						filterable: false,
						disableColumnMenu: true,
						renderHeader,
						renderCell: column.hasKana ? (
							options.showKana ? (params) => renderer.name(params.value) :
							(params) => renderer.nameKanji(params.value)
						) : undefined,
						...column,
						width: state.width ?? column.width
					}
				}
			)
			const added = Object.entries(columnsDef).filter(
				([field, column]) => {
					return column.hidden !== true && !columnsState.some(state => state.field === field)
				}
			)
			if (added.length > 0 || removed.length > 0) {
				setColumnsState(
					columnsState
						.filter(state => !removed.includes(state.field))
						.concat(added.map(
							([field, column]) => {
								return {
									field,
									show: column.defaultShow ?? true,
									hideable: column.hideable === false ? false : true
								}
							}
						))
						.slice()
				)
			}
			return columns
		},
		[columnsState, columnsDef, renderHeader, options, setColumnsState]
	)
	const rows = useMemo(
		() => dataList.flatMap(
			(record, id) => {
				if (filters.some(filter => {
					return isFilterd(record, filter, columnsDef)
				})) return []

				const row: DataRowModel = { id }
				Object.entries(columnsDef).forEach(
					([fileld, column]) => {
						const value = column.value(record) ?? ''
						if (Array.isArray(value)) {
							row[fileld] = value.join('\n')
						} else {
							row[fileld] = value
						}
					}
				)
				return row
			}
		),
		[dataList, filters, columnsDef]
	)
	const getRowData = useCallback(
		(id: GridRowId) => {
			const index = id as number
			return (index >= 0 && dataList.length > index) ? dataList[index] : null
		},
		[dataList]
	)

	const onColumnResize: GridEventListener<'columnResize'> = useCallback(
		(params/*, event, details*/) => {
			const { width, colDef: { field } } = params
			//console.log(`columnResize: ${field}: ${width}`)
			setColumnsState(...[
				columnsState.map(state => state.field === field ? { ...state, width } : state)
			])
		},
		[columnsState, setColumnsState]
	)

	const onCellClick: GridEventListener<"cellClick"> = useCallback(
		(params/*, event, details*/) => {
			const { field, id } = params
			if (onActiveColumn) onActiveColumn(field)
			if (onSelectedRow) onSelectedRow(getRowData(id))
			const value = apiRef.current?.getCellValue(id, field)
			setFocusedItem({ field, rowId: id, value })
			if (isSmallMedia === false) {
				const fieldDef = columnsDef[field]
				setShowFilterManage(fieldDef?.filterType ? true : false)
			}
		},
		[isSmallMedia, apiRef, columnsDef, getRowData, setFocusedItem, onActiveColumn, onSelectedRow, setShowFilterManage]
	)

	const onRowDoubleClick: GridEventListener<"rowDoubleClick"> = useCallback(
		(params, event/*, details*/) => {
			const { id } = params
			event.defaultMuiPrevented = true
			event.defaultPrevented = true
			if (onDblClick) {
				const row = getRowData(id)
				if (row) onDblClick(row)
			}
		},
		[onDblClick, getRowData]
	)

	const doDownload = useCallback(
		async (params: GridDownloadParams) => {
			const api = apiRef.current
			if (!api) return

			setDownloading(true)
			const data = makeDownloadData(api, columns, columnsDef)
			if (data) {
				if (params.format === 'clipboard') {
					await clipCsvData(data)
				} else {
					await downloadData({
						data,
						format: params.format,
						fileName: params.fileName,
						sheetName
					})
				}
			}
			setDownloading(false)
			setModal(null)
		},
		[apiRef, columns, columnsDef, sheetName, setDownloading]
	)

	useEffect(
		() => {
			functionsRef.current = {
				dataLength: rows.length,
				download: () => setModal('download')
			}
		},
		[functionsRef, setModal, rows]
	)

	return (
		<>
			{others.loading && <Loading percent={progress}/>}

			<Stack
				direction={isSmallMedia ? "column" : "row"}
				boxSizing="border-box"
				height="100%"
				maxHeight="100%"
				flexGrow={1}
				overflow="hidden"
				spacing={isSmallMedia ? 0 : 1}
			>
				<DataGrid
					apiRef={apiRef}
					density="compact"
					columnHeaderHeight={40}
					showCellVerticalBorder
					showColumnVerticalBorder
					hideFooterSelectedRowCount
					columnBufferPx={100}
					columns={columns}
					rows={rows ?? []}
					getRowHeight={() => 'auto'}
					sx={{
						flexGrow: 1,
						[`& .${gridClasses.cell}`]: {
							py: 1,
						},
						...columnStyles
					}}
					disableRowSelectionOnClick
					onColumnResize={onColumnResize}
					onCellClick={onCellClick}
					onRowDoubleClick={onRowDoubleClick}
					{...others}
				/>

				<Slide
					direction={isSmallMedia ? "up" : "left"}
					in={showColumnManage}
					mountOnEnter unmountOnExit
				>
					<Paper
						variant="outlined"
						square
						sx={isSmallMedia ? {
							boxSizing: 'border-box',
							width: '100%',
							position: 'relative',
							height: '30vh'
						} : {
							boxSizing: 'border-box',
							height: '100%',
							position: 'relative',
							width: '15em'
						}}
					>
						<GridColumnManageInspector
							columnsDef={columnsDef}
							columnsState={columnsState}
							setColumnsState={setColumnsState}
							onClose={() => setShowColumnManage(false)}
						/>
					</Paper>
				</Slide>

				{keepFilterColumns.map(
					field => (focusedItem !== null && focusedItem.field !== field) ? (
						<Paper
							key={`keepfilter-${field}`}
							variant="outlined"
							square
							sx={isSmallMedia ? {
								boxSizing: 'border-box',
								width: '100%',
								position: 'relative',
								height: '40vh'
							} : {
								boxSizing: 'border-box',
								height: '100%',
								position: 'relative',
								width: '20em'
							}}
						>
							<GridFilterInspector
								key={`filter-${field}`}
								field={field}
								rowId={focusedItem?.rowId ?? null}
								columnsDef={columnsDef}
								dataList={dataList}
								filters={filters}
								setFilters={setFilters}
								onClose={() => setShowFilterManage(false)}
								isKeeped={true}
								keepFilterColumns={keepFilterColumns}
								setKeepFilterColumns={setKeepFilterColumns}
							/>
						</Paper>
					) : <></>
				)}

				<Slide
					direction={isSmallMedia ? "up" : "left"}
					in={showFilterManage}
					mountOnEnter unmountOnExit
				>
					<Paper
						variant="outlined"
						key="filter"
						square
						sx={isSmallMedia ? {
							boxSizing: 'border-box',
							width: '100%',
							position: 'relative',
							height: '40vh'
						} : {
							boxSizing: 'border-box',
							height: '100%',
							position: 'relative',
							width: '20em'
						}}
					>
						{focusedItem ? (
							<GridFilterInspector
								key={`filter-${focusedItem.field}`}
								field={focusedItem.field}
								rowId={focusedItem.rowId}
								columnsDef={columnsDef}
								dataList={dataList}
								filters={filters}
								setFilters={setFilters}
								onClose={() => setShowFilterManage(false)}
								isKeeped={keepFilterColumns.includes(focusedItem.field)}
								keepFilterColumns={keepFilterColumns}
								setKeepFilterColumns={setKeepFilterColumns}
							/>
						) : <></>}
					</Paper>
				</Slide>
			</Stack>

			{modal === 'download' ? (
				<GridDownloadDialog
					downloading={downloading}
					fileName={baseFileName}
					onDownload={doDownload}
					onClose={() => setModal(null)}
				/>
			) : <></>}
		</>
	)
}
